import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { listSubscriptionPlans } from '@/lib/api/core';

/**
 * useMembershipPlans — catálogo de planes de membresía vía
 * `GET /v1/subscriptions/plans` (api-core).
 *
 * Notas de mapeo:
 * - `display_name` es el nombre visible; fallback a `name`.
 * - `price` está en CENTAVOS COP (ciclo mensual) → se normaliza a pesos
 *   enteros (`/100`). El endpoint no expone un par monthly/annual separado;
 *   el precio anual queda `null` (la UI usa el mensual). Ver GAP-04.
 * - puntos: `welcome_points` (mensual) y `monthly_bonus_points`.
 * - El tono visual y el `targetAudience` se derivan por slug.
 */

export type MembershipPlanTone = 'neutral' | 'success' | 'info' | 'gold';

export type MembershipPlan = {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  targetAudience: string;
  monthlyPriceCents: number | null;
  annualPriceCents: number | null;
  features: string[];
  benefits: string[];
  /** Alias histórico de `pointsMonthly` (welcome / ciclo mensual). */
  welcomePoints: number;
  /** Puntos por suscribirse ciclo mensual (= welcome). ROUND12 admin R26. */
  pointsMonthly: number;
  /** Puntos por suscribirse ciclo anual (= monthly × 10). ROUND12 admin R26. */
  pointsAnnual: number;
  tone: MembershipPlanTone;
  recommended: boolean;
};

type PlanPresentation = {
  tone: MembershipPlanTone;
  targetAudience: string;
};

const PRESENTATION: Record<string, PlanPresentation> = {
  gratuito: {
    tone: 'neutral',
    targetAudience: 'Ideal si estás conociendo First Class.',
  },
  premium: {
    tone: 'gold',
    targetAudience: 'Para quienes viven la marca con frecuencia.',
  },
  firstclass: {
    tone: 'success',
    targetAudience: 'La experiencia más completa, sin compromisos.',
  },
};

function presentationFor(slug: string): PlanPresentation {
  return (
    PRESENTATION[slug] ?? {
      tone: 'info',
      targetAudience: 'Elegí el plan que mejor te acompañe.',
    }
  );
}

/** Convierte un valor en centavos COP a pesos enteros. */
function centsToWholeCop(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'string' ? Number.parseFloat(value) : Number(value);
  return Number.isFinite(n) ? Math.round(n / 100) : null;
}

async function fetchMembershipPlans(): Promise<MembershipPlan[]> {
  const data = await listSubscriptionPlans();

  return data
    .filter((r) => r.active !== false)
    .map((r) => {
      const slug = typeof r.slug === 'string' && r.slug.trim() ? r.slug.trim() : String(r.id);
      const displayName =
        typeof r.display_name === 'string' && r.display_name.trim()
          ? r.display_name.trim()
          : typeof r.name === 'string' && r.name.trim()
            ? r.name.trim()
            : 'Plan';
      const present = presentationFor(slug);
      const monthlyCop = centsToWholeCop(r.price);
      const pointsMonthly = Number.isFinite(Number(r.welcome_points))
        ? Number(r.welcome_points)
        : 0;
      // Sin desglose anual en el endpoint (GAP-04): puntos anuales = mensual × 10.
      const pointsAnnual = pointsMonthly * 10;
      return {
        id: String(r.id),
        slug,
        name: displayName,
        tagline:
          typeof r.description === 'string' && r.description.trim()
            ? r.description.trim()
            : present.targetAudience,
        targetAudience: present.targetAudience,
        monthlyPriceCents: monthlyCop,
        annualPriceCents: null,
        features: [],
        benefits: [],
        welcomePoints: pointsMonthly,
        pointsMonthly,
        pointsAnnual,
        tone: present.tone,
        recommended: false,
      };
    });
}

export function useMembershipPlans(): UseQueryResult<MembershipPlan[]> {
  return useQuery({
    queryKey: ['membership-plans', 'subscription-plans', 'active'],
    queryFn: fetchMembershipPlans,
    staleTime: 5 * 60_000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}
